import './add-client.scss'

import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { useMachine } from '@xstate/react'

import {
  Close,
  GenericValidationMessages,
  Icon,
  IconButton,
  LinkIcon,
  OperationButton,
  Section,
  Space
} from '@/components/core-v2'

import { ClientsState } from '../clients/clients.state'
import { Client, Fee, Mutation } from '../types'
import { ClientEditor } from './client-editor'
import { addClientMachine } from './state/add-client-machine.state'

export type Props = {
  clientState: ClientsState
  onAddComplete: () => void
  onClose: () => void
}

export function AddClient({ clientState, onAddComplete, onClose }: Props) {
  const [state, send] = useMachine(addClientMachine)

  const {
    availableFeeTypes,
    counterpartyIds: availableCptyIds,
    depositInstruments: availableDepositInstruments,
    loanInstruments: availableLoanInstruments,
    strategies
  } = clientState

  const mutateClient = (mutation: Mutation<Client>) => send({ type: 'MUTATE', mutation })
  const mutateNoteFee = (mutation: Mutation<Fee>) => send({ type: 'MUTATE_NOTE_FEE', mutation })
  const mutateDelta1Fee = (mutation: Mutation<Fee>) => send({ type: 'MUTATE_DELTA1_FEE', mutation })
  const mutateCPLeverageFee = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_CPLEV_FEE', mutation })
  const mutateRiskManagedFee = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_RISK_MANAGED_FEE', mutation })
  const mutateVCLeverageFee = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_VCLEV_FEE', mutation })
  const mutateTermProtFee1 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_TERM_PROT_FEE1', mutation })
  const mutateTermProtFee2 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_TERM_PROT_FEE2', mutation })
  const mutateTermProtFee3 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_TERM_PROT_FEE3', mutation })
  const mutateTermProtFee4 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_TERM_PROT_FEE4', mutation })
  const mutateContProtFee1 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_CONT_PROT_FEE1', mutation })
  const mutateContProtFee2 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_CONT_PROT_FEE2', mutation })
  const mutateContProtFee3 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_CONT_PROT_FEE3', mutation })
  const mutateContProtFee4 = (mutation: Mutation<Fee>) =>
    send({ type: 'MUTATE_CONT_PROT_FEE4', mutation })

  if (state.matches('saveSuccess')) {
    return (
      <div className="add-a-new-client-confirm">
        <header className="CustomMuiTitleContainer-root">
          <Typography color="textPrimary">The new client has been created</Typography>
        </header>
        <div className="body">
          Business units, users and fees can now be added to the new client
        </div>
        <div className="block button">
          <OperationButton operationType="progress" onClick={() => onAddComplete()}>
            Ok
          </OperationButton>
        </div>
      </div>
    )
  }

  const StyledNewClientWrapper = styled.div`
    width: 800px;
    display: flex;
    flex-direction: column;
    max-height: calc(80vh - 50px);
  `

  const StyledNewClientBlock = styled.div``

  return (
    <StyledNewClientWrapper key="add_new_client_container">
      <Section type="secondary">
        <Space isflex alignItems="center" justifyContent="space-between" pl={5} py={6}>
          <Typography color="textPrimary">Add a client</Typography>
          <Close onClick={() => onClose()} />
        </Space>
      </Section>
      <StyledNewClientBlock key="client_edit_form_container">
        <ClientEditor
          key="client_editor_form"
          availableCptyIds={availableCptyIds}
          availableDepositInstruments={availableDepositInstruments}
          availableFeeTypes={availableFeeTypes}
          availableLoanInstruments={availableLoanInstruments}
          client={state.context.client}
          clientName={state.context.client.clientName}
          errors={state.context.errors}
          existingClient={false}
          newCPLeverageFee={state.context.newCPLeverageFee}
          newContProtFee1={state.context.newContProtFee1}
          newContProtFee2={state.context.newContProtFee2}
          newContProtFee3={state.context.newContProtFee3}
          newContProtFee4={state.context.newContProtFee4}
          newDelta1Fee={state.context.newDelta1Fee}
          newNoteFee={state.context.newNoteFee}
          newRiskManagedFee={state.context.newRiskManagedFee}
          newTermProtFee1={state.context.newTermProtFee1}
          newTermProtFee2={state.context.newTermProtFee2}
          newTermProtFee3={state.context.newTermProtFee3}
          newTermProtFee4={state.context.newTermProtFee4}
          newVCLeverageFee={state.context.newVCLeverageFee}
          strategies={strategies}
          onMutate={mutateClient}
          onMutateCPLevFee={mutateCPLeverageFee}
          onMutateContProtFee1={mutateContProtFee1}
          onMutateContProtFee2={mutateContProtFee2}
          onMutateContProtFee3={mutateContProtFee3}
          onMutateContProtFee4={mutateContProtFee4}
          onMutateDelta1Fee={mutateDelta1Fee}
          onMutateNoteFee={mutateNoteFee}
          onMutateRiskManagedFee={mutateRiskManagedFee}
          onMutateTermProtFee1={mutateTermProtFee1}
          onMutateTermProtFee2={mutateTermProtFee2}
          onMutateTermProtFee3={mutateTermProtFee3}
          onMutateTermProtFee4={mutateTermProtFee4}
          onMutateVCLevFee={mutateVCLeverageFee}
        />
      </StyledNewClientBlock>
      <GenericValidationMessages errorMessages={state.context.errorMessages} />
      <Section type="quaternary">
        <Space isflex justifyContent="space-between" px={5} py={6}>
          <LinkIcon
            disabled={false}
            icon={<Icon name="chevronLeft" size="small" />}
            iconPosition="left"
            text="Cancel and go back"
            onClick={() => onClose()}
          />
          <IconButton
            disabled={!state.context.isSaveEnabled}
            icon={<Icon name="circleAdd" size="default" />}
            iconPosition="left"
            operationType="progress"
            text="Add client"
            onClick={() => send({ type: 'SAVE', client: state.context.client })}
          />
        </Space>
      </Section>
    </StyledNewClientWrapper>
  )
}
